<script>
    import TextField from '@itslearning/prometheus/assets/inputs/TextField/v1/TextField.svelte';
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import IconButton from "@itslearning/prometheus/assets/inputs/IconButton/v1/IconButton.svelte";
    import Checkbox from '@itslearning/prometheus/assets/inputs/Checkbox/v1/Checkbox.svelte';
    import Select from '@itslearning/prometheus/assets/inputs/Select/v1/Select.svelte';
    import Banner from '@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte'
    import Link from '@itslearning/prometheus/assets/Navigation/Link/v1/Link.svelte';
    import Page from './Page/Page.svelte';
    import { txt } from '../i18n/i18n';
    import { saveQuestion, hasRespondents } from '../api';
    import { getAlternativeText, getOptions } from '../LikertScale';
    import { navigation, questions, getNewId, isPreviewMode, permissions } from '../stores';
    import { navigateToQuestionListPage, navigateToPreviewSurveyPage } from '../navigation';
    import { onMount } from 'svelte';
    import { Question } from "../models/Question";
    import { Alternative } from '../models/Alternative';
    import { stripHtml } from '../helpers';

    const emptyQuestion = {
        text: '',
        type: 'Open',
        alternatives: [
            { alternativeId: getNewId(), text: '' },
            { alternativeId: getNewId(), text: '' },
            { alternativeId: getNewId(), text: '' },
        ],
        matrixRows: [
            { matrixRowId: getNewId(), text: '' },
            { matrixRowId: getNewId(), text: '' }
        ]
    };
    const maxNumberOfAlternativesPerQuestion = 50;
    const maxNumberOfMatrixRowsPerQuestion = 50;

    let respondentsStatus = undefined;
    let isEdit = false;
    let editQuestionIndex = 0;
    let message = { text: '', type: 'info' };
    let question = new Question({ questionId: getNewId(), ...emptyQuestion });
    let canSelectMultipleAlternatives = false;
    let alternativeType = "Custom";
    let isSaving = false;
    let isSavingAndNew = false;

    $: hasGlobalResponse = respondentsStatus && respondentsStatus.hasGlobalResponse === true;
    $: visibleAlternatives = question.alternatives.filter(a => !a.isDeleted);
    $: visibleMatrixRows = question.matrixRows.filter(r => !r.isDeleted);
    $: canEdit = $permissions.canModify && !hasGlobalResponse;
    $: pageTitle = isEdit 
                    ? txt(canEdit ? 'Edit_question_x_of_y' : 'View_question_x_of_y', [editQuestionIndex + 1, $questions.length])
                    : txt('Add_question_x', [$questions.length + 1]);

    onMount(async () => {
        // make a clone of the question, so we don't modify directly in store (in case user presses cancel)
        isEdit = $navigation.questionId !== null;
        if (isEdit) {
            editQuestionIndex = $questions.findIndex((q) => q.questionId === $navigation.questionId);
            question = new Question($questions[editQuestionIndex]);
        } else {
            question = new Question({ questionId: getNewId(), ...emptyQuestion });
        }
        if (question.hasHtml) {
            question.text = stripHtml(question.text, true);
        }
        canSelectMultipleAlternatives = question.isMultipleResponse();
        await getRespondentStatus();
    });

    async function getRespondentStatus() {
        try {
            respondentsStatus = await hasRespondents();
        } catch { }
    }

    function addAlternative() {
        const altId = getNewId();
        question.alternatives = [
            ...question.alternatives,
            { alternativeId: altId },
        ];
        setTimeout(() => { document.getElementById('prom-input-alt' + altId).focus(); }, 100);
    }

    function alternativeTypeChanged() {
        for (let i = 0; i < question.alternatives.length; i++) {
            let alternative = question.alternatives[i];
            if (i > 4) {
                alternative.isDeleted = true;
            } else {
                alternative.isDeleted = false;
                alternative.text = getAlternativeText(i, alternativeType);
                if (i > 2) {
                    if (!alternative.text || alternative.text == '') {
                        alternative.isDeleted = true;
                    }
                }
                question.alternatives[i] = alternative;
            }
        }
        for (let i = question.alternatives.length; i < 5; i++) {
            let alternative = {};
            alternative.alternativeId = getNewId();
            alternative.text = getAlternativeText(i, alternativeType);
            alternative.isDeleted = alternative.text == '';
            question.alternatives.push(alternative);
        }
    }

    function removeAlternative(alternativeId) {
        const alt = question.alternatives.find(a => a.alternativeId === alternativeId);
        if (alt) {
            alt.isDeleted = true;
            question.alternatives = question.alternatives;
        }
    }

    function addRow() {
        const rowId = getNewId();
        question.matrixRows = [
            ...question.matrixRows,
            { matrixRowId: rowId },
        ];
        setTimeout(() => { document.getElementById('prom-input-row' + rowId).focus(); }, 100);
    }

    function removeRow(matrixRowId) {
        const row = question.matrixRows.find(r => r.matrixRowId === matrixRowId);
        if (row) {
            row.isDeleted = true;
            question.matrixRows = question.matrixRows;
        }    
    }

    function isInputValid() {
        let isValid = isValidText(question.text);
        if (question.type === 'MultipleChoice' || question.type === 'Matrix') {
            isValid = isValid && question.alternatives && question.alternatives.length >= 2;
            for (let i = 0; i < question.alternatives.length; i++) {
                isValid = isValid && (isValidText(question.alternatives[i].text) || question.alternatives[i].isDeleted);
            }
        }
        if (question.type === 'Matrix') {
            isValid = isValid && question.matrixRows && question.matrixRows.length >= 1;
            for (let i = 0; i < question.matrixRows.length; i++) {
                isValid = isValid && (isValidText(question.matrixRows[i].text) || question.matrixRows[i].isDeleted);
            }
        }
        return isValid;
    }

    function isValidText(text) {
        return text && text.trim().length > 0;
    }

    async function saveAndNew() {
        if (!isInputValid()) {
            return;
        }
        if (isSavingAndNew) {
            return;
        }
        isSavingAndNew = true;
        let result = await save(false);
        // reset flag here, so that it will be possible to create new question (click 'Save' button)
        isSaving = false;
        isSavingAndNew = false;
        if (!result){
            return;
        } 
        message = { text: txt('Question_has_been_saved'), type: 'info' };
        let alts = [];
        const type = question.type;
        if (question.alternatives) {
            question.alternatives.forEach((a) => {
                const alt = new Alternative(a);
                alt.alternativeId = getNewId();
                alts.push(alt);
            });
        }
        isEdit = false;
        question = new Question(emptyQuestion);
        question.type = type;
        if (alts.length > 0) {
            question.alternatives = alts;
        }
        focusHeadingCount += 1;
    }

    async function save(navigateAway = true) {
        if (!isInputValid()) {
            return false;
        }
        try {
            if (isSaving) {
                return false;
            }
            isSaving = true;
            question.maxAlternativesToSelect = question.type === 'MultipleChoice' && !canSelectMultipleAlternatives ? 1 : 0;
            question.usingPredefinedAlternatives = (question.type === 'MultipleChoice' || question.type === 'Matrix') && alternativeType !== 'Custom';
            let result = await saveQuestion(question)
            question = new Question(result.questions[0]);
            if (isEdit) {
                // find the current question and update it
                const updatedQuestions = $questions.map(q => q.questionId === question.questionId ? question : q);
                questions.set(updatedQuestions);
            }
            else{
                // add a new question 
                questions.update(currentQuestions => {currentQuestions = [...currentQuestions, question]; return currentQuestions;});
            }
            if (navigateAway) {
                performPageNavigation();
            }
            return true;
        } catch {
            message = { text: txt('Unknown_error'), type: 'warning' };
            await getRespondentStatus();
            // to be able to try again set isSaving = false
            // we don't modify isSaving in success path, since it will be possible to click 'Save' multiple times (on slow connection)
            isSaving = false;
            return false;
        }
    }
    
    function performPageNavigation() {
        if (isPreviewMode()) {
            navigateToPreviewSurveyPage(hasGlobalResponse);
        }
        else {
            navigateToQuestionListPage();
        }
    }

    function questionTypeChanged(e) {
        question.type = e.detail;
    }
    let focusHeadingCount = 1;  // we change this to trigger new heading focus on every "save and new" click
</script>


<Page title={pageTitle} headingPosition="insideContentBlock" focusHeading={focusHeadingCount >= 1}>
    {#if !canEdit}
        <div class="toolbar">
            <Link kind="standalone" iconClass="back-link" href="#question_list" on:click={() => navigateToQuestionListPage()} text={txt('Back_to_list_of_questions')} />
        </div>
    {/if}

    <Banner visible={message.text} close={txt('Close')} type={message.type} text={message.text} closable={false} />
    <Banner type="info" text={txt('Not_possible_to_edit_questions_message')} visible={hasGlobalResponse} closable={true} close={txt('Close')}/>
    {#if canEdit && question.hasHtml}
        <Banner visible="true" type="warning" text="{txt('Imported_rich_text_edit_warning')}" closable={false} />
    {/if}
 
    <form on:submit|preventDefault class="proto-form">
        <TextField id="question"
                    disabled={!canEdit} 
                    rows={Math.max(3, Math.min(10, question.getNumberOfLines()))}
                    type="area" 
                    required={true} 
                    label={txt('Question')} 
                    bind:value={question.text}
                    on:keyup={() => { message.text = ''; }}
                    maxlength=60000 />

        {#if !isEdit}
            <Select label={txt('Question_type')} bind:value={question.type} on:change={questionTypeChanged}>
                <option value="Open">{txt('Open_question')}</option>
                <option value="YesNo">{txt('Yes_no_question')}</option>
                <option value="MultipleChoice">{txt('Multiple_choice_question')}</option>
                <option value="Matrix">{txt('Matrix_question')}</option>
            </Select>
        {/if}

        {#if canEdit && (question.type === 'MultipleChoice' || question.type === 'Matrix')}
            <Select label={txt('Answer_type')} bind:value={alternativeType} on:change={alternativeTypeChanged}>
                {#each getOptions() as option}
                    <option value="{option.value}">{option.name}</option>
                {/each}
            </Select>
        {/if}

        {#if question.type === 'MultipleChoice'}
            <Checkbox disabled={!canEdit} label={txt('Allow_respondents_to_select_more_than_one_alternative')} bind:checked={canSelectMultipleAlternatives} />
        {/if}

        {#if question.type === 'MultipleChoice' || question.type === 'Matrix'}
            <fieldset>
                <legend>{txt(question.type === 'Matrix' ? 'Alternatives_columns' : 'Alternatives')}</legend>
            
                {#each visibleAlternatives as alternative, i (alternative.alternativeId)}
                    <div class="alt-row">
                        <TextField
                            disabled={!canEdit}
                            type="text"
                            id="{'alt' + alternative.alternativeId}"
                            maxlength=500
                            required={true}
                            bind:value={alternative.text}
                            label={txt('Alternative_x', [ i + 1 ])}
                            labelHidden={true}
                        />
                        {#if canEdit}
                            <div class="alt-remove-btn-container">
                                <IconButton
                                    text={txt('Remove_alternative_x', [i + 1])}
                                    htmlType="button"
                                    type="plain"
                                    iconClass="remove-alt-button"
                                    iconOnly={true}                                    
                                    disabled={visibleAlternatives.length < 3}
                                    on:click={() => removeAlternative(alternative.alternativeId)}
                                />
                            </div>
                        {/if}
                    </div>
                {/each}
            
                {#if canEdit && visibleAlternatives.length < maxNumberOfAlternativesPerQuestion}
                    <IconButton
                        text={txt('Add_alternative')}
                        htmlType="button"
                        type="plain"
                        iconClass="add-alt-button"
                        on:click={addAlternative}
                    />
                {/if}
            </fieldset>
        {/if}

        {#if question.type === 'Matrix'}
            <fieldset>
                <legend>{txt('Statements_rows')}</legend>
            
                {#each visibleMatrixRows as row, i (row.matrixRowId)}
                    <div class="alt-row"> 
                        <TextField
                            disabled={!canEdit}
                            type="text"
                            id="{'row' + row.matrixRowId}"
                            maxlength=500
                            required={true}
                            bind:value={row.text}
                            label={txt('Statement_x', [i + 1])}
                            labelHidden={true}
                        />
                        {#if canEdit}
                            <div class="alt-remove-btn-container">
                                <IconButton
                                    text={txt('Remove_statement_x', [i + 1])}
                                    htmlType="button"
                                    type="plain"
                                    iconClass="remove-alt-button"
                                    iconOnly={true}
                                    disabled={visibleMatrixRows.length < 3}
                                    on:click={() => removeRow(row.matrixRowId)}
                                />
                            </div>
                        {/if}
                    </div>
                {/each}

                {#if canEdit && visibleMatrixRows.length < maxNumberOfMatrixRowsPerQuestion}
                    <IconButton
                        text={txt('Add_statement')}
                        htmlType="button"
                        type="plain"
                        iconClass="add-alt-button"
                        on:click={addRow}
                    />
                {/if}
            </fieldset>
        {/if}

        {#if canEdit}
            <div class="footer-actions">
                <div class="footer-actions-left">
                    <Button text={txt('Save')} htmlType="submit" type="primary" on:click={save} loading={isSaving && !isSavingAndNew} />
                    {#if !isPreviewMode()}
                        <Button text={txt('Save_and_new')} htmlType="submit" on:click={saveAndNew} loading={isSaving && isSavingAndNew} />
                    {/if}
                    <Button text={txt('Cancel')} htmlType="button" type="plain" on:click={performPageNavigation} />
                </div>
            </div>
        {/if}
    </form>
</Page>